import DocumentPage from './DocumentPage';

export const libraryDocument = {
  slug: 'library',
  route: '/library',
  title: 'Library',
  category: 'Academics',
  format: 'DOCX',
  description:
    'Library management in EduX covers configuration masters, library cards, media and media units, return and issue operations, media movements, requests, and dashboard visibility for librarians.',
  metrics: [
    { label: 'Primary focus', value: 'Library operations' },
    { label: 'Core records', value: 'Cards, media, movements' },
    { label: 'Coverage', value: 'Students and faculty' },
  ],
  workflow: [
    'Configure media types, publishers, authors, and library card types before daily operations begin.',
    'Create library cards, media, and media units so issue and return activity can be tracked correctly.',
    'Monitor media movements, queue requests, purchase requests, and the library dashboard for ongoing control.',
  ],
  sections: [
    {
      id: 'library-configuration',
      title: 'Library Configuration',
      body: [
        'EduX provides the facility to configure library management.',
        'To begin this, you need to configure some data in order to make it working. We refer to this as Configuration.',
        'You can configure the data for library like Media Type, Publisher, Authors, and Library Card Type.',
      ],
      detailCards: [
        {
          title: 'Configuration masters',
          bullets: [
            'Media Type: that is assigned to a book, for example course book, reference book, or magazine.',
            'Publisher: list of publications of which books you have in library.',
            'Authors: list of authors whose books are available in library.',
            'Library Card Type: specify the type of library card, for example Bachelor Degree Card or Master Degree Card.',
          ],
        },
      ],
      subsections: [
        {
          id: 'publishers',
          title: 'Publishers',
          body: [
            'The media available in library may have different publishers. Sometimes book of particular publisher is preferred by teachers. In such cases you must have publisher related information.',
            'Go to Library -> Configuration -> Publishers. Click on New to create a new publisher.',
          ],
          figure: {
            src: '/assets/doc-images/library/image2.png',
            alt: 'Publisher record showing name, address, and linked media entries',
            caption:
              'Publisher record showing the publication name, address, and automatically linked media entries.',
          },
          bullets: [
            'Name: name of publication house.',
            'Address: name of company whose address is considered as address of the publication.',
            'Media(s): media are added in the list automatically when a media is created with particular publisher.',
          ],
        },
        {
          id: 'authors',
          title: 'Authors',
          body: [
            'Media is written by one or more authors. To have the details of authors while creating a media, you can configure the author details.',
            'Go to Library -> Configuration -> Authors. Click on New to create a new author.',
          ],
          figure: {
            src: '/assets/doc-images/library/image3.png',
            alt: 'Author record showing address and linked media entries',
            caption:
              'Author record showing the author name, address, and the automatically linked media list.',
          },
          bullets: [
            'Name: name of the author.',
            'Address: name of person or company whose address is referred as the address of the author.',
            'Media(s): list of medias written by the author. These lines are created automatically when a media is created.',
          ],
        },
        {
          id: 'media-type',
          title: 'Media Type',
          body: [
            'The Media Type are used for specifying the sub-category of the media, for example Course Book, Reference Books, or Magazine.',
            'Go to Library -> Configuration -> Media Type. Click on New to create a new media type.',
          ],
          figure: {
            src: '/assets/doc-images/library/image1.png',
            alt: 'Media type record showing name and code',
            caption:
              'Media Type record showing the media type name and code fields.',
          },
          bullets: [
            'Name: assign a name to a media type.',
            'Code: assign a code to a media type.',
          ],
        },
        {
          id: 'library-card-types',
          title: 'Library Card Types',
          body: [
            'Library cards can be available in different types as it can be used by students, faculties, alumni etc. Those library cards may have different rules application, so it is preferable that you configure this type of library cards.',
            'Go to Library -> Configuration -> Library Card Types. Click on New to create a new library card type.',
          ],
          figure: {
            src: '/assets/doc-images/library/imaged.png',
            alt: 'Library card type record showing media limits, duration, and penalty amount',
            caption:
              'Library Card Type record showing name, number of medias allowed, duration, and penalty amount per day.',
          },
          bullets: [
            'Name: title of library card type.',
            'Number of medias Allowed: specify the number of medias allowed on a card.',
            'Duration: number of days for which the media is allowed to be kept with the person.',
            'Penalty Amount Per Day: penalty given per day to the person who returns the media after due date.',
          ],
        },
      ],
    },
    {
      id: 'library-cards',
      title: 'Library Cards',
      body: [
        'We can create a library card and assign it to a person.',
        'Go to Library -> Configuration -> Library Cards.',
        'Click on New to create a new library card.',
      ],
      figure: {
        src: '/assets/doc-images/library/imagea.png',
        alt: 'Library card record assigned to a student',
        caption:
          'Library Card record showing the unique number, issue date, card type, and linked student details.',
      },
      gallery: [
        {
          src: '/assets/doc-images/library/imageb.png',
          alt: 'Student profile more menu showing the Library action',
          caption: 'Student profile menu showing the Library action from the More menu.',
        },
        {
          src: '/assets/doc-images/library/imagec.png',
          alt: 'Media movement details opened from the student library button',
          caption:
            'Media Movement details shown after clicking the Library button from the student profile.',
        },
      ],
      detailCards: [
        {
          title: 'Details of Library Cards Form',
          bullets: [
            'Number: number is the unique card number.',
            'Issue Date: issue date is on which date the card was issued to the person. By default todays date appears in this field.',
            'Card Type: type of card being created which can be selected or created from the list.',
            'Type: either faculty or student to whom the card is allocated. By default Student appears in this field.',
            'When a new library card is issued to a student, it can be allocated to student or faculty in their profile.',
            'After clicking the Library button, the system will display the Media Movement details.',
          ],
        },
      ],
    },
    {
      id: 'media-and-units',
      title: 'Media and Media Units',
      body: [
        'To accommodate all the media details available in your library, you can create records of the medias and individual media units for circulation.',
      ],
      subsections: [
        {
          id: 'media',
          title: 'Media',
          body: [
            'Go to Library -> All Media -> Media. Click on New to create a new media.',
          ],
          figure: {
            src: '/assets/doc-images/library/image9.png',
            alt: 'Media record showing authors, publishers, edition, and media metadata',
            caption:
              'Media record showing the media title, author, publisher, edition, page count, cost, and media type details.',
          },
          bullets: [
            'Name: provide the name of the book in Name field. It is mandatory field.',
            'Author(s): mention the name of authors who has written the book in Authors field. You can select multiple authors here.',
            'Publisher(s): mention the name of publication houses in the Publishers field.',
            'ISBN and Internal Code: provide ISBN and internal code of the book.',
            'Edition: specify the edition of the book.',
            'Tag(s): provide the appropriate tags in Tags field.',
            'Media Type: select media type.',
          ],
        },
        {
          id: 'media-unit',
          title: 'Media Unit',
          body: [
            'When a new media unit is created, OpenEduCat assumes that media unit is available in library. So now a librarian can perform operations like issue media unit and return media unit.',
            'Thus once a media unit is created, its default state is Available.',
            'Go to Library -> All Media -> Media Units. Click on New to create a new media unit.',
          ],
          gallery: [
            {
              src: '/assets/doc-images/library/imagee.png',
              alt: 'Media unit in available state ready for issue',
              caption:
                'Media Unit in Available state before any issue activity is performed.',
            },
            {
              src: '/assets/doc-images/library/imagef.png',
              alt: 'Media unit in issued state with movement lines',
              caption:
                'Media Unit in Issued state showing the linked movement line details.',
            },
          ],
          bullets: [
            'Name: provide the name for Media Unit.',
            'Media: select the media from list for which media unit is created.',
            'Barcode: provide the barcode of the media.',
            'Media Type: select media type.',
          ],
        },
        {
          id: 'return-media',
          title: 'Return Media',
          body: [
            'To return a media unit, click on Return Media button. It opens a wizard where you can mention the actual return date of the media unit. By default todays date appears in this field.',
            'Click on Proceed button to actually return a media unit. It closes the wizard and changes the state of media unit from Issued to Available.',
          ],
          gallery: [
            {
              src: '/assets/doc-images/library/image10.png',
              alt: 'Return Media wizard with actual return date field',
              caption:
                'Return Media wizard used to enter the actual return date before proceeding.',
            },
            {
              src: '/assets/doc-images/library/image11.png',
              alt: 'Media unit after return process showing returned done state',
              caption:
                'Media Unit after return processing, showing movement history and the Returned Done state.',
            },
          ],
        },
      ],
    },
    {
      id: 'media-movements',
      title: 'Media Movements',
      body: [
        'EduX records the movements of all the books in the system. Whenever any media unit is issued or returned its movement is recorded in the Media Movement.',
        'Go to Library -> All Media -> Media Movements. Click on New to create a new media movement.',
      ],
      figure: {
        src: '/assets/doc-images/library/image5.png',
        alt: 'Media movement record showing issued media details and student or faculty information',
        caption:
          'Media Movement record showing media, media unit, student or faculty details, issue date, due date, actual return date, and penalty fields.',
      },
      detailCards: [
        {
          title: 'Details of Media Movement',
          bullets: [
            'Media: select name of the media for media movement.',
            'Media Unit: select media unit for media movement.',
            'Media Type: select media type for media movement.',
            'Media Queue Count: total media requests for media.',
            'Invoice: penalty invoice reference.',
            'Library Card: library card to which media unit is issued.',
            'Student/Faculty: whether the media unit is issued to student or faculty.',
            'Student: based on selection in Student/Faculty name of student or faculty appears.',
            'Issue Date and Due Date: issue date and return date during which media unit is issued and is to be returned. By default todays date appears in issue date field.',
            'Actual Return Date: date during which media unit is returned by the holder.',
            'Penalty: in case the media unit is returned late penalty is calculated by EduX based on number of late days.',
          ],
        },
      ],
      subsections: [
        {
          id: 'movement-available',
          title: 'Available',
          body: [
            'Initially when a movement is created it is in Available state.',
          ],
        },
        {
          id: 'movement-issued',
          title: 'Issued',
          body: [
            'Click on Issue Media button to issue a media unit. It changes the state from Available to Issued.',
          ],
          figure: {
            src: '/assets/doc-images/library/image6.png',
            alt: 'Media unit workflow showing transition into issued state',
            caption:
              'Workflow actions on a media unit showing the state transition into Issued.',
          },
        },
        {
          id: 'movement-returned',
          title: 'Returned',
          body: [
            'If penalty is calculated then state changes from Issued to Returned.',
            'If media is returned after due date then penalty is calculated as shown in image.',
          ],
          gallery: [
            {
              src: '/assets/doc-images/library/image7.png',
              alt: 'Media movement state bar showing returned done status',
              caption: 'State bar showing the media movement reaching the returned stage.',
            },
            {
              src: '/assets/doc-images/library/image8.png',
              alt: 'Returned media record with penalty details',
              caption:
                'Returned media record showing student details, dates, and calculated penalty values.',
            },
          ],
        },
      ],
    },
    {
      id: 'media-requests',
      title: 'Media Requests',
      body: [
        'There are two situations when you need to make a request for book.',
      ],
      bullets: [
        'A media is already allocated to someone else: create Media Queue Request.',
        'A media is not available in library: create a new Media Purchase Request.',
      ],
      subsections: [
        {
          id: 'media-queue-requests',
          title: 'Media Queue Requests',
          body: [
            'Go to Library -> All Media -> Media Queue Requests. Click on New to create a new media queue request.',
          ],
          figure: {
            src: '/assets/doc-images/library/image12.png',
            alt: 'Media queue request showing user, media, dates, and accepted workflow state',
            caption:
              'Media Queue Request showing user, media, from and to dates, and the Accepted workflow state.',
          },
          detailCards: [
            {
              title: 'Details of Media Queue Request Form',
              bullets: [
                "Sequence Number: automatically generated sequence number by EduX. By default '/' appears here.",
                'User: name of student or faculty who has requested for a media. By default the logged-in person appears here.',
                'Media: name of the book.',
                'From Date: date from which the person wants to have media. By default todays date appears here.',
                'To Date: last date till which person can keep that media with him.',
              ],
            },
            {
              title: 'Workflow of Media Queue Request',
              bullets: [
                'By default the state is Request which means the book request is just created.',
                'Click on Accept button to accept request. It changes the state from Request to Accepted.',
                'In case if you want to reject the request, click on Reject button. The state will be changed to Rejected.',
                'If media is in reject state and you want to request it again click on Request Again.',
              ],
            },
          ],
        },
        {
          id: 'media-purchase-requests',
          title: 'Media Purchase Requests',
          body: [
            'Student or faculty can request for a new media. That is why you can create a new purchase media request.',
            'Go to Library -> All Media -> Media Purchase Requests.',
            'Click on New to create a new media purchase request.',
          ],
          figure: {
            src: '/assets/doc-images/library/image13.png',
            alt: 'Media purchase request showing title, media type, course, author, publisher, and draft workflow state',
            caption:
              'Media Purchase Request showing title, media type, course, subject, authors, publisher, requested by, edition, and the draft workflow state.',
          },
          detailCards: [
            {
              title: 'Details of Media Purchase Request Form',
              bullets: [
                'Title: name of the media.',
                'Media Type: select media type whether it is course book or reference book etc.',
                'Course: name of the course whose students can use this media.',
                'Subject: specify the subject of media.',
                'Author(s): specify the name of author in Author field.',
                'Publisher(s): name of the publisher of this media.',
                'Requested By: select person who requested the media.',
                'Edition: specify the edition of the media you want to have in library.',
              ],
            },
            {
              title: 'Workflow of Media Purchase Request',
              bullets: [
                'Default state of the media request is Draft. Click on Request Media button for confirming the book request. It changes the state from Draft to Requested.',
                'Click on Accept to accept the request. It changes the state from Requested to Accepted.',
                'Click on Reject to reject the request.',
              ],
            },
          ],
        },
      ],
    },
    {
      id: 'library-dashboard',
      title: 'Library Dashboard',
      body: [
        'To see library dashboard go to Library -> Library Dashboard.',
        'Library dashboard is a graphical view for librarian. He can have quick overview of used medias of current day and current month.',
        'Librarian can see all the details of issued and available medias. These are fetched from media movements records.',
      ],
    },
  ],
  related: ['student', 'faculty', 'parent'],
};

export default function LibraryPage() {
  return <DocumentPage document={libraryDocument} />;
}
